import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { clearStoredTokens, getStoredToken } from '@/store/authStore';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://dev.justhi.boosters.es/api/v1';

/// Ventana (ms) durante la cual toleramos fallos 5xx del refresh-token
/// antes de cerrar la sesión y mandar al usuario al login. Sin este
/// timeout, un backend caído dejaba al usuario en estado "zombie":
/// `isAuthenticated: true` pero todas las peticiones devolviendo 401.
const REFRESH_FAILURE_TIMEOUT_MS = 30_000;

let refreshFailureStartedAt: number | null = null;

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Request interceptor: inject access token.
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getStoredToken('access_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: on 401, try refresh once.
let isRefreshing = false;
let pendingQueue: Array<(token: string | null) => void> = [];

function flushQueue(token: string | null) {
  pendingQueue.forEach((cb) => cb(token));
  pendingQueue = [];
}

apiClient.interceptors.response.use(
  (response) => {
    refreshFailureStartedAt = null;
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (!original || original._retry) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    if (status !== 401) {
      return Promise.reject(error);
    }

    const refreshToken = getStoredToken('refresh_token');
    if (!refreshToken) {
      clearStoredTokens();
      if (typeof window !== 'undefined') window.location.href = '/login';
      return Promise.reject(error);
    }

    original._retry = true;

    if (isRefreshing) {
      // Otro request ya está refrescando — esperamos al resultado.
      return new Promise((resolve, reject) => {
        pendingQueue.push((newToken) => {
          if (!newToken) {
            reject(error);
            return;
          }
          if (original.headers) original.headers.Authorization = `Bearer ${newToken}`;
          resolve(apiClient(original));
        });
      });
    }

    isRefreshing = true;
    try {
      const { data } = await axios.post(
        `${API_URL}/auth/refresh`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const newAccess = data.access_token as string;
      const newRefresh = data.refresh_token as string;
      if (typeof window !== 'undefined') {
        const wasLocal = window.localStorage.getItem('access_token');
        const storage = wasLocal ? window.localStorage : window.sessionStorage;
        storage.setItem('access_token', newAccess);
        storage.setItem('refresh_token', newRefresh);
      }
      refreshFailureStartedAt = null;
      flushQueue(newAccess);
      if (original.headers) original.headers.Authorization = `Bearer ${newAccess}`;
      return apiClient(original);
    } catch (refreshErr) {
      const refreshStatus = (refreshErr as AxiosError)?.response?.status;
      if (refreshStatus === 401 || refreshStatus === 403) {
        clearStoredTokens();
        if (typeof window !== 'undefined') window.location.href = '/login';
        flushQueue(null);
        return Promise.reject(refreshErr);
      }
      const now = Date.now();
      if (refreshFailureStartedAt === null) refreshFailureStartedAt = now;
      if (now - refreshFailureStartedAt > REFRESH_FAILURE_TIMEOUT_MS) {
        clearStoredTokens();
        if (typeof window !== 'undefined') window.location.href = '/login';
      }
      flushQueue(null);
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
